import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Heart, MessageCircle, Send, Music2 } from "lucide-react";

const SCENES = [
  { img: "/media/run.jpg", words: ["Trucco", "n.1:", "muoviti", "20", "minuti", "ogni", "giorno"] },
  { img: "/media/salad.jpg", words: ["Trucco", "n.2:", "riempi", "metà", "piatto", "di", "verdure"] },
  { img: "/media/walk.jpg", words: ["Trucco", "n.3:", "bevi", "acqua", "appena", "sveglio"] },
];
const STEPS = 9;

const Captions = ({ words, idx }) => (
  <p className="font-display pl-4 pr-14 text-left text-[1.05rem] font-extrabold leading-snug text-white [text-shadow:0_2px_8px_rgba(0,0,0,0.6)]">
    {words.map((w, i) => (
      <span
        key={i}
        className={`mx-[2px] inline-block rounded-md px-1 transition-all duration-200 ${
          i === idx ? "scale-110 bg-[#0066FF]" : ""
        } ${i > idx ? "opacity-0" : "opacity-100"}`}
      >
        {w}
      </span>
    ))}
  </p>
);

const Rail = () => (
  <div className="absolute bottom-28 right-3 z-20 flex flex-col items-center gap-4 text-white">
    {[Heart, MessageCircle, Send].map((Icon, i) => (
      <span key={i} className="grid h-9 w-9 place-items-center rounded-full bg-white/15 backdrop-blur-md">
        <Icon className="h-[18px] w-[18px]" />
      </span>
    ))}
  </div>
);

export const PhoneDemo = ({ playing = true, className = "" }) => {
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!playing) return undefined;
    const t = setInterval(() => setTick((x) => x + 1), 420);
    return () => clearInterval(t);
  }, [playing]);

  const sceneIdx = Math.floor(tick / STEPS) % SCENES.length;
  const scene = SCENES[sceneIdx];
  const step = tick % STEPS;

  return (
    <div
      className={`relative aspect-[9/19] w-full rounded-[2.7rem] bg-[#111111] p-[9px] shadow-[0_40px_80px_-30px_rgba(17,17,17,0.55),0_0_0_1px_rgba(255,255,255,0.06)_inset] ${className}`}
      data-testid="phone-demo"
    >
      <div className="relative h-full w-full overflow-hidden rounded-[2.2rem] bg-black">
        <AnimatePresence initial={false}>
          <motion.img
            key={sceneIdx}
            src={scene.img}
            alt="Anteprima del video demo"
            className="absolute inset-0 h-full w-full object-cover"
            initial={{ opacity: 0, scale: 1.18 }}
            animate={{ opacity: 1, scale: 1.04 }}
            exit={{ opacity: 0 }}
            transition={{ opacity: { duration: 0.6 }, scale: { duration: 4.2, ease: "linear" } }}
          />
        </AnimatePresence>
        <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-transparent to-black/70" />
        <div className="absolute left-1/2 top-2 z-30 h-[22px] w-[86px] -translate-x-1/2 rounded-full bg-black" />

        <div className="absolute inset-x-4 top-10 z-20 flex gap-1">
          {SCENES.map((_, i) => (
            <span key={i} className="h-[3px] flex-1 overflow-hidden rounded-full bg-white/30">
              <span
                className="block h-full bg-white transition-[width] duration-300 ease-linear"
                style={{ width: i < sceneIdx ? "100%" : i === sceneIdx ? `${((step + 1) / STEPS) * 100}%` : "0%" }}
              />
            </span>
          ))}
        </div>

        <div className="absolute inset-x-0 top-16 z-20 flex justify-center">
          <span className="font-display rounded-full bg-white px-3.5 py-1.5 text-[0.8rem] font-bold text-[#111111] shadow-lg">
            3 trucchi per dimagrire
          </span>
        </div>

        <Rail />

        <div className="absolute inset-x-0 bottom-[6.2rem] z-20">
          <Captions words={scene.words} idx={Math.min(step, scene.words.length - 1)} />
        </div>

        <div className="absolute inset-x-4 bottom-5 z-20 flex items-center gap-2.5 text-white">
          <span className="grid h-8 w-8 place-items-center rounded-full bg-white">
            <img src="/logo-mimis.png" alt="" className="h-4 w-auto" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-[0.78rem] font-semibold">@il.tuo.profilo</p>
            <p className="flex items-center gap-1 text-[0.7rem] text-white/85">
              <Music2 className="h-3 w-3" /> Voce italiana · Giulia
            </p>
          </div>
          <span className="flex h-5 items-end gap-[3px]" aria-hidden>
            {[0, 1, 2, 3].map((i) => (
              <span
                key={i}
                className="eq-bar block w-[3px] rounded-full bg-white"
                style={{ height: "100%", animationDelay: `${i * 0.15}s`, animationPlayState: playing ? "running" : "paused" }}
              />
            ))}
          </span>
        </div>
      </div>
    </div>
  );
};
